import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Layout, ToggleDarkMode, SidebarUser, useSidebar } from '@thegpvc/ui';
import { LayoutDashboard, List, Settings as SettingsIcon, BarChart3, FileText, FolderGit2, Lightbulb, LogOut } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

// Hoisted icon components to avoid recreation on each render
const DashboardIcon = <LayoutDashboard className="w-4 h-4" />;
const SessionsIcon = <List className="w-4 h-4" />;
const InsightsIcon = <Lightbulb className="w-4 h-4" />;
const ProjectsIcon = <FolderGit2 className="w-4 h-4" />;
const ReportsIcon = <FileText className="w-4 h-4" />;
const StatsIcon = <BarChart3 className="w-4 h-4" />;
const SettingsIconEl = <SettingsIcon className="w-4 h-4" />;

const sidebarItems = [
  { id: 'dashboard', label: 'Home', icon: DashboardIcon, path: '/' },
  { id: 'sessions', label: 'Sessions', icon: SessionsIcon, path: '/sessions' },
  { id: 'projects', label: 'Projects', icon: ProjectsIcon, path: '/projects' },
  { id: 'insights', label: 'Insights', icon: InsightsIcon, path: '/insights' },
  { id: 'reports', label: 'Reports', icon: ReportsIcon, path: '/reports' },
  { id: 'stats', label: 'Stats', icon: StatsIcon, path: '/stats' },
  { id: 'settings', label: 'Settings', icon: SettingsIconEl, path: '/settings' },
];

function SidebarFooter({ user, onLogout }: { user: { name: string; email: string }; onLogout: () => void }) {
  const { isCollapsed } = useSidebar();

  return (
    <>
      <SidebarUser name={user.name} email={user.email} />
      <div className="px-3 pb-2">
        <button
          onClick={onLogout}
          className="flex items-center gap-3 px-2 py-2 rounded-md text-sm font-medium transition-colors w-full text-navy-600 dark:text-navy-300 hover:text-navy-900 dark:hover:text-white hover:bg-navy-100 dark:hover:bg-navy-700"
          title={isCollapsed ? 'Sign out' : undefined}
        >
          <span className="flex-shrink-0 w-5 h-5">
            <LogOut className="w-4 h-4" />
          </span>
          <span
            className={`flex-1 truncate text-left whitespace-nowrap transition-opacity duration-300 ${isCollapsed ? 'opacity-0' : 'opacity-100'}`}
          >
            Sign out
          </span>
        </button>
      </div>
    </>
  );
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  return (
    <Layout
      title="Dixie"
      onNavigateHome={() => navigate('/')}
      isHomePage={isHomePage}
      showBackButton={!isHomePage}
      headerRight={<ToggleDarkMode mode="dark" />}
      maxWidth="wide"
      sidebar={{
        items: sidebarItems.map((item) => ({
          ...item,
          isActive: location.pathname === item.path ||
                    (item.path !== '/' && location.pathname.startsWith(item.path)),
          onClick: () => navigate(item.path),
        })),
        footer: user ? (
          <SidebarFooter user={{ name: user.name, email: user.email }} onLogout={logout} />
        ) : undefined,
      }}
    >
      <Outlet />
    </Layout>
  );
}
